/**
 * The screen taking a hit.
 *
 * A collision used to announce itself with a card above the console. It read
 * fine on its own and badly in practice: it sat in the same place as the docking
 * prompt, the jump lock and the radio, so the one message that arrives without
 * warning was the one most likely to be underneath something. And it asked you
 * to read a sentence at exactly the moment you needed to be flying.
 *
 * So the hit is shown where a hit belongs — at the edges of the view, for as
 * long as it takes to register and no longer. Nothing to read, nothing to
 * dismiss, nothing that can be covered up by another panel. The console still
 * carries the actual number, on the hull lamp, for when you want it.
 *
 * ── Why it flashes rather than pulses ───────────────────────────────────────
 *
 * On instantly, off slowly. A symmetrical fade reads as an effect; an abrupt
 * arrival and a long decay reads as impact, because that is the shape of the
 * event it stands for. The decay is eased so the last of it goes quietly rather
 * than switching off.
 */

import { useEffect, useRef, useState } from 'react';

/** How long a hit stays on screen, in seconds. */
const GLANCING_FADE = 0.85;
const SEVERE_FADE = 1.4;

/** How much of the view each severity is willing to take. */
const GLANCING_STRENGTH = 0.55;
const SEVERE_STRENGTH = 1;

export interface DamageVignetteProps {
  /**
   * The last charged hit, or null.
   *
   * Read by `at` rather than by identity: the flight loop bumps that per hit it
   * actually billed for, so two collisions in a row are two flashes even when
   * everything else about them matches.
   */
  hit: { severity: 'glancing' | 'severe' | 'destroyed'; at: number } | null;
}

export function DamageVignette({ hit }: DamageVignetteProps) {
  const [level, setLevel] = useState(0);
  const severity = useRef<'glancing' | 'severe' | 'destroyed'>('glancing');
  const shown = useRef(-1);

  useEffect(() => {
    if (!hit || hit.at === shown.current) return;

    shown.current = hit.at;
    severity.current = hit.severity;

    const heavy = hit.severity !== 'glancing';
    const fade = heavy ? SEVERE_FADE : GLANCING_FADE;
    const peak = heavy ? SEVERE_STRENGTH : GLANCING_STRENGTH;

    // Straight to full, then down. Driven off the clock rather than a CSS
    // transition so a second hit mid-fade restarts cleanly from full.
    setLevel(peak);

    let raf = 0;
    const started = performance.now();
    const step = () => {
      const elapsed = (performance.now() - started) / 1000;
      if (elapsed >= fade) {
        setLevel(0);
        return;
      }
      // Cubic out: most of the brightness goes early, the tail lingers.
      const left = 1 - elapsed / fade;
      setLevel(peak * left * left * left);
      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [hit]);

  if (level <= 0.001) return null;

  const heavy = severity.current !== 'glancing';
  // Red for a wound, amber for a scrape — the same two colours the hull lamp
  // uses, so the screen and the console are not telling different stories.
  const tint = heavy ? '255,45,60' : '255,140,60';

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        // Clear in the middle, so it never sits between the pilot and what they
        // are about to hit next.
        background: `radial-gradient(ellipse at center, rgba(${tint},0) 38%, rgba(${tint},${(level * 0.55).toFixed(3)}) 100%)`,
        // A hard hit whites the edges out briefly as well as reddening them.
        boxShadow: heavy ? `inset 0 0 ${Math.round(level * 220)}px rgba(${tint},${(level * 0.5).toFixed(3)})` : undefined,
        zIndex: 5,
      }}
    />
  );
}
